import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Radar, Play, Plus, Trash2, Clock } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const TYPE_COLORS = {
  companies_house: 'text-blue-400 border-blue-500/30',
  csv_import: 'text-amber-400 border-amber-500/30',
  directory: 'text-green-400 border-green-500/30',
};

export default function BackofficeScout() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'companies_house', config_json: { sic_code: '43210', region: 'West Midlands', limit: 10 }, is_active: true });

  const load = () => {
    setLoading(true);
    api.get('/backoffice/lead-sources')
      .then(r => setSources(r.data.lead_sources || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runSource = async (id) => {
    setRunning(id);
    try {
      const r = await api.post(`/backoffice/lead-sources/${id}/run`);
      toast.success(`Imported ${r.data.imported} prospects`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setRunning(null);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/backoffice/lead-sources', form);
      toast.success('Lead source created');
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead source?')) return;
    await api.delete(`/backoffice/lead-sources/${id}`);
    toast.success('Deleted');
    load();
  };

  const runEnrich = async () => {
    const r = await api.post('/backoffice/jobs/enrich');
    toast.success(`Enriched ${r.data.enriched} prospects`);
  };

  const runScore = async () => {
    const r = await api.post('/backoffice/jobs/score');
    toast.success(`Scored ${r.data.scored} prospects`);
  };

  const runDrafts = async () => {
    const r = await api.post('/backoffice/jobs/generate-drafts');
    toast.success(`Generated ${r.data.drafts_created} drafts`);
  };

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-scout">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>LEAD SCOUT</h1>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={() => setShowCreate(true)} data-testid="create-source-btn">
            <Plus className="w-4 h-4 mr-1" /> New Source
          </Button>
        </div>

        <div className="space-y-2 mb-8">
          {loading ? (
            [...Array(2)].map((_, i) => <div key={i} className="h-20 bg-slate-900 border border-slate-800 rounded-sm animate-pulse" />)
          ) : sources.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">No lead sources configured</p>
          ) : (
            sources.map(s => (
              <Card key={s.id} className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Radar className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-200">{s.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[s.type] || 'text-slate-400 border-slate-600'}`}>{s.type}</Badge>
                        {s.last_run_at && (
                          <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Last run: {new Date(s.last_run_at).toLocaleDateString()}
                          </span>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${s.is_active ? 'text-green-400 border-green-500/30' : 'text-slate-500 border-slate-700'}`}>
                          {s.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-8" onClick={() => runSource(s.id)} disabled={running === s.id} data-testid={`run-source-${s.id}`}>
                      <Play className="w-3 h-3 mr-1" /> {running === s.id ? 'Running...' : 'Run Now'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-400 h-7 w-7 p-0" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pipeline Actions */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 uppercase tracking-wider">Pipeline Jobs</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm" onClick={runEnrich} data-testid="run-enrich-btn">
              Enrich Prospects
            </Button>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm" onClick={runScore} data-testid="run-score-btn">
              Score Prospects
            </Button>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm" onClick={runDrafts} data-testid="run-drafts-btn">
              Generate Drafts
            </Button>
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-200 max-w-md">
            <DialogHeader><DialogTitle className="text-white">New Lead Source</DialogTitle><DialogDescription className="text-slate-400 text-sm">Configure a new lead discovery source.</DialogDescription></DialogHeader>
            <div className="grid gap-3">
              <div><Label className="text-slate-400 text-xs">Name *</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="source-name-input" /></div>
              <div>
                <Label className="text-slate-400 text-xs">Type</Label>
                <select className="w-full mt-1 h-8 bg-slate-800 border border-slate-700 text-slate-200 rounded-sm text-sm px-2" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  <option value="companies_house">Companies House</option>
                  <option value="csv_import">CSV Import</option>
                  <option value="directory">Directory</option>
                </select>
              </div>
              <div><Label className="text-slate-400 text-xs">SIC Code</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.config_json.sic_code} onChange={e => setForm({...form, config_json: {...form.config_json, sic_code: e.target.value}})} /></div>
              <div><Label className="text-slate-400 text-xs">Region</Label><Input className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm mt-1" value={form.config_json.region} onChange={e => setForm({...form, config_json: {...form.config_json, region: e.target.value}})} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="text-slate-400" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={handleCreate} disabled={!form.name} data-testid="save-source-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  );
}
